import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { jwtDecode } from 'jwt-decode';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import ImageHostProvider from '@/contexts/ImageHostProvider';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { getAdminUser } from './services/backend/admin';
import appRouter from './../config/routes';
import NoPermission from '@/pages/403';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
    appRouter?: any;
    loading?: boolean;
    fetchUserInfo: () => Promise<(API.CurrentUser) | undefined>;
}> {
    const fetchUserInfo = async () => {
        const token = localStorage.getItem('jwt_token');
        if (!token) return undefined;
        try {
            const decoded: any = jwtDecode(token);
            const memberID = decoded.memberID;
            if (memberID) {
                const data = await getAdminUser(memberID);
                return {
                    name: data.admin_name,
                    email: data.account,
                    userid: memberID,
                    avatar: decoded.avatar,
                    permissions: data.permissions || [],
                    ...decoded,
                } as API.CurrentUser;
            }
            return undefined;
        } catch {
            return undefined;
        }
    };

    const { location } = history;
    if (![loginPath].includes(location.pathname)) {
        const currentUser = await fetchUserInfo();
        return {
            fetchUserInfo,
            currentUser,
            appRouter,
            settings: defaultSettings as Partial<LayoutSettings>,
        };
    }

    return {
        fetchUserInfo,
        appRouter,
        settings: defaultSettings as Partial<LayoutSettings>,
    };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
    return {
        actionsRender: () => [],
        avatarProps: {
            src: initialState?.currentUser?.avatar,
            title: <AvatarName />,
            render: (_, avatarChildren) => {
                return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
            },
        },
        footerRender: () => <Footer />,
        onPageChange: () => {
            const { location } = history;
            const token = localStorage.getItem('jwt_token');
            let isExpired = false;
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    if (decoded.exp && Date.now() >= decoded.exp * 1000) {
                        isExpired = true;
                    }
                } catch {
                    isExpired = true;
                }
            }
            if (!token || isExpired) {
                localStorage.removeItem('jwt_token');
                history.replace(loginPath);
                return;
            } else if (!initialState?.currentUser && location.pathname !== loginPath) {
                history.push(loginPath);
                return;
            }
        },
        bgLayoutImgList: [],
        links: [],
        unAccessible: <NoPermission />,
        menuHeaderRender: undefined,
        childrenRender: children => {
            return <ImageHostProvider>{children}</ImageHostProvider>;
        },
        ...initialState?.settings,
    };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
    baseURL: process.env.BASE_URL || 'http://localhost:8080',
    ...errorConfig,
    requestInterceptors: [
        (url, options) => {
            const token = localStorage.getItem('jwt_token');
            if (token) {
                options.headers = {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                };
            }
            return { url, options };
        },
    ],
    responseInterceptors: [
        response => {
            if (response.status === 403) {
                localStorage.removeItem('jwt_token');
                window.location.href = '/login';
            }
            return response;
        },
    ],
};
